import React, { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { fetchJson } from "../../services/api";
import { ErrorStats } from "../types";
import { PageHeader, SectionCard, Loader, ErrorMsg, refreshBtnStyle } from "../components/Common";
import "./ErrorsPage.css";

const COLORS = ["#ef4444", "#f97316", "#f59e0b", "#84cc16", "#22c55e"];

const ErrorsPage: React.FC = () => {
  const [data, setData]       = useState<ErrorStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    fetchJson<ErrorStats>("/monitor/errors/", { top: 8 })
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  if (loading) return <Loader />;
  if (error)   return <ErrorMsg msg={error} onRetry={load} />;
  if (!data)   return null;

  return (
    <div>
      <PageHeader title="Errors" subtitle="Failing endpoints and error breakdown" />
      <div className="errors-page__toolbar">
        <button onClick={load} style={refreshBtnStyle}>↻ Refresh</button>
      </div>

      <div className="errors-page__grid">
        <SectionCard title="Top Failing Endpoints">
          {data.top_failing_endpoints.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.top_failing_endpoints} layout="vertical" margin={{ left: 10, right: 20 }}>
                <XAxis type="number" tick={{ fill: "#64748b", fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="endpoint" tick={{ fill: "#94a3b8", fontSize: 10 }} width={160} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v) => [v, "Failed calls"]} />
                <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                  {data.top_failing_endpoints.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </SectionCard>

        <SectionCard title="By Error Type">
          {data.by_error_type.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.by_error_type} layout="vertical" margin={{ left: 10, right: 20 }}>
                <XAxis type="number" tick={{ fill: "#64748b", fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="error_type" tick={{ fill: "#94a3b8", fontSize: 10 }} width={160} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v) => [v, "Occurrences"]} />
                <Bar dataKey="count" fill="#f97316" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </SectionCard>
      </div>

      <div className="errors-page__secondary-grid">
        <SectionCard title="By HTTP Status Code">
          <table className="errors-page__table">
            <thead>
              <tr>
                {["Status Code", "Count"].map((h) => (
                  <th key={h} className="errors-page__table-head">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.by_status_code.length === 0
                ? <tr><td colSpan={2} className="errors-page__empty">No data</td></tr>
                : data.by_status_code.map((row) => (
                  <tr key={row.status_code} style={{ borderBottom: "1px solid var(--border)" }}>
                    <td className="errors-page__table-cell">
                      <span style={{
                        fontFamily: "monospace",
                        fontWeight: 600,
                        color: row.status_code >= 500 ? "#f87171" : row.status_code >= 400 ? "#fbbf24" : "var(--muted)",
                      }}>
                        {row.status_code}
                      </span>
                    </td>
                    <td className="errors-page__table-cell errors-page__table-cell--value">{row.count}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </SectionCard>

        <SectionCard title="Errors by School">
          <table className="errors-page__table">
            <thead>
              <tr>
                {["School Code", "Errors"].map((h) => (
                  <th key={h} className="errors-page__table-head">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.by_school.length === 0
                ? <tr><td colSpan={2} className="errors-page__empty">No data</td></tr>
                : data.by_school.map((row) => (
                  <tr key={row.school_code} style={{ borderBottom: "1px solid var(--border)" }}>
                    <td className="errors-page__table-cell" style={{ color: "var(--primary)", fontFamily: "monospace" }}>{row.school_code}</td>
                    <td className="errors-page__table-cell" style={{ color: "var(--danger)", fontWeight: 600 }}>{row.count}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </SectionCard>
      </div>
    </div>
  );
};

export default ErrorsPage;

const tooltipStyle: React.CSSProperties = {
  background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12, color: "var(--text)",
};

const Empty: React.FC = () => (
  <div className="errors-page__empty">No error data yet</div>
);
